/* SAP shared cadence-modal controller.
 *
 * Drives the per-product One-time vs Auto-Replenish + 30/45/60 cadence modals
 * used by the four-steps, ritual-upsell, and shop-now sections. Previously this
 * logic was copy-pasted (identically) into all three section files; it now lives
 * here once.
 *
 * Each section calls SAPCadenceModal.init(sectionEl, opts) from its own IIFE so
 * Shopify theme-editor section re-renders re-wire correctly (per-section scope,
 * not a one-time document scan).
 *
 * opts:
 *   onAdded        - optional fn (may be async) run after a successful /cart/add.js,
 *                    e.g. the section's own refreshCartCounts().
 *   openCartDrawer - if true, click the header CartDrawer trigger after add
 *                    (homepage sections). Default false (collection pages).
 *
 * The global is defined idempotently so multiple section script tags loading the
 * same asset don't redefine it.
 */
window.SAPCadenceModal =
  window.SAPCadenceModal ||
  (function () {
    function wireModalOpen(root) {
      root.querySelectorAll("[data-modal-target]").forEach(function (btn) {
        // Idempotency: init() may run more than once (boot retry + theme-editor
        // re-render). Guard so each trigger is bound exactly once — a double bind
        // would open the dialog twice / double-submit.
        if (btn.dataset.sapModalWired === "1") return;
        btn.dataset.sapModalWired = "1";
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          // Stop the click bubbling to an enclosing card link (ritual-upsell posters
          // wrap the trigger in a clickable card; harmless no-op elsewhere).
          e.stopPropagation();
          var dialog = document.getElementById(btn.dataset.modalTarget);
          if (dialog && typeof dialog.showModal === "function") dialog.showModal();
        });
      });
    }

    function wireDialog(dialog) {
      // Idempotency guard (see wireModalOpen).
      if (dialog.dataset.sapDialogWired === "1") return;
      dialog.dataset.sapDialogWired = "1";
      dialog.querySelectorAll('[data-action="close-modal"]').forEach(function (btn) {
        btn.addEventListener("click", function () {
          dialog.close();
        });
      });
      dialog.addEventListener("click", function (e) {
        if (e.target === dialog) dialog.close();
      });

      var planRadios = dialog.querySelectorAll("input[data-plan-type]");
      var dayPicker = dialog.querySelector("[data-day-picker]");
      var dayButtons = dialog.querySelectorAll("[data-day]");
      var sellingPlanInput = dialog.querySelector("[data-selling-plan-input]");
      var submitPriceEl = dialog.querySelector("[data-submit-price]");
      var subPriceEl = dialog.querySelector("[data-plan-sub-price]");
      var cadenceHintEl = dialog.querySelector("[data-cadence-days]");
      var onetimePrice = ((dialog.querySelector("[data-modal-price]") || {}).textContent || "").trim();
      var activeDayBtn = dialog.querySelector("[data-day].is-active") || dayButtons[0];

      function setActiveDay(btn) {
        if (!btn) return;
        activeDayBtn = btn;
        dayButtons.forEach(function (b) {
          b.classList.toggle("is-active", b === btn);
          b.setAttribute("aria-pressed", b === btn ? "true" : "false");
        });
        if (sellingPlanInput) sellingPlanInput.value = btn.dataset.planId;
        if (subPriceEl) subPriceEl.textContent = btn.dataset.planPrice + "/mo";
        var subRadio = dialog.querySelector('input[data-plan-type="subscription"]');
        if (subRadio && subRadio.checked && submitPriceEl) {
          submitPriceEl.textContent = btn.dataset.planPrice + "/mo";
        }
        if (cadenceHintEl) cadenceHintEl.textContent = btn.dataset.day + " days";
      }

      function setPlanType(type) {
        var hint = dialog.querySelector("[data-cadence-hint]");
        if (type === "subscription") {
          if (dayPicker) dayPicker.style.display = "";
          if (hint) hint.style.display = "";
          if (activeDayBtn && sellingPlanInput) sellingPlanInput.value = activeDayBtn.dataset.planId;
          if (activeDayBtn && submitPriceEl) submitPriceEl.textContent = activeDayBtn.dataset.planPrice + "/mo";
        } else {
          if (dayPicker) dayPicker.style.display = "none";
          if (hint) hint.style.display = "none";
          if (sellingPlanInput) sellingPlanInput.value = "";
          if (submitPriceEl) submitPriceEl.textContent = onetimePrice;
        }
      }

      planRadios.forEach(function (radio) {
        radio.addEventListener("change", function () {
          if (radio.checked) setPlanType(radio.dataset.planType);
        });
      });
      dayButtons.forEach(function (btn) {
        btn.addEventListener("click", function () {
          setActiveDay(btn);
        });
      });

      var checkedRadio = dialog.querySelector("input[data-plan-type]:checked");
      if (checkedRadio) setPlanType(checkedRadio.dataset.planType);
    }

    function wireForm(form, opts) {
      // Idempotency guard (see wireModalOpen) — a double bind would add twice.
      if (form.dataset.sapWired === "1") return;
      form.dataset.sapWired = "1";

      form.addEventListener("submit", async function (e) {
        e.preventDefault();
        var dialog = form.closest(".four-steps-modal");
        var submitBtn = form.querySelector("[data-submit-btn]");
        var errorEl = form.querySelector("[data-form-error]");

        if (submitBtn) submitBtn.disabled = true;
        if (errorEl) {
          errorEl.hidden = true;
        }

        try {
          var formData = new FormData(form);
          // Strip empty selling_plan so Shopify treats it as a one-time purchase.
          if (!formData.get("selling_plan")) formData.delete("selling_plan");

          /* Locate Aurora's cart drawer + its REAL section id at runtime (never
           hard-coded) so the add response carries the freshly-rendered
           cart-drawer section, which we hand to the drawer's own purchaseHandler.
           This mirrors assets/sap-pdp-cadence-box.js exactly. Previously this
           path clicked the header cart trigger after a urlencoded add — but a
           urlencoded body does NOT trigger the Section Rendering API, and the
           trigger re-opens the drawer from its cached <template> clone, so the
           just-added item did not appear (intermittent empty drawer). */
          var drawer = document.querySelector("cart-drawer");
          var sectionId = (drawer && drawer.dataset.sectionId) || "cart-drawer";

          /* Build the add the way Aurora's native product form does: a FormData
           body with Accept: application/javascript + X-Requested-With and NO
           explicit Content-Type (the browser sets the multipart boundary). Only
           request the cart-drawer section when we actually intend to open it. */
          var body = new FormData();
          body.append("id", formData.get("id"));
          /* Clamp to 1 — a typed "0" survives `|| "1"` and /cart/add.js
             no-ops it with a 200 (mirrors sap-pdp-cadence-box.js doAdd). */
          body.append("quantity", String(Math.max(1, parseInt(formData.get("quantity"), 10) || 1)));
          var sp = formData.get("selling_plan");
          if (sp) body.append("selling_plan", sp);
          if (opts.openCartDrawer) {
            body.append("sections", sectionId);
            body.append("sections_url", window.location.pathname);
          }

          var res = await fetch("/cart/add.js", {
            method: "POST",
            credentials: "same-origin",
            headers: {
              Accept: "application/javascript",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: body,
          });

          var responseText = await res.text();
          var responseJson = null;
          try {
            responseJson = JSON.parse(responseText);
          } catch (_) {}

          if (!res.ok) {
            throw new Error(
              (responseJson && (responseJson.description || responseJson.message)) ||
                responseText.slice(0, 200) ||
                "HTTP " + res.status,
            );
          }

          if (typeof opts.onAdded === "function") {
            try {
              await opts.onAdded();
            } catch (_) {}
          }
          if (dialog && dialog.open) dialog.close();
          document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));

          if (opts.openCartDrawer) {
            /* Native refresh + open: hand the freshly-rendered cart-drawer section
             to Aurora's purchaseHandler (a PARSED Document, like Aurora's own
             parseHTML). It emits 'update-nodes' so every cart sub-block re-renders
             with the new item, then opens the drawer — the exact path a native
             add takes. Fall back to the trigger click only if the section HTML or
             purchaseHandler is unavailable (item is already in the server cart). */
            var sectionHTML = responseJson && responseJson.sections && responseJson.sections[sectionId];
            if (drawer && typeof drawer.purchaseHandler === "function" && sectionHTML) {
              var parsedDrawer = new DOMParser().parseFromString(sectionHTML, "text/html");
              drawer.purchaseHandler(parsedDrawer, responseJson);
            } else {
              var cartTrigger = document.querySelector('[data-sidebar-button][data-sidebar-id="CartDrawer"]');
              if (cartTrigger) cartTrigger.click();
            }
          }
        } catch (err) {
          if (errorEl) {
            var errorText = errorEl.querySelector("[data-error-text]");
            if (errorText) errorText.textContent = err.message || "Could not add to bag.";
            else errorEl.textContent = err.message || "Could not add to bag.";
            errorEl.hidden = false;
          }
        } finally {
          if (submitBtn) submitBtn.disabled = false;
        }
      });
    }

    function init(root, opts) {
      if (!root) return;
      opts = opts || {};
      wireModalOpen(root);
      root.querySelectorAll(".four-steps-modal").forEach(wireDialog);
      root.querySelectorAll("form[data-four-steps-form]").forEach(function (form) {
        wireForm(form, opts);
      });
    }

    return { init: init };
  })();
